/**
 * Native OS notifications (the macOS/Windows banners Chrome raises) for things
 * that arrive while the portal is not the window someone is looking at.
 *
 * Browser-only, and deliberately conservative: a banner is an interruption on
 * someone's whole desktop, not a badge in a corner of a page they chose to open.
 * The rules below exist so it stays worth having.
 */

export interface DesktopNotificationInput {
  title: string;
  body: string;
  /**
   * Collapses repeats. Two messages in the same thread replace one banner rather
   * than stacking — otherwise a colleague typing five lines produces five
   * banners, and the officer turns the whole thing off.
   */
  tag: string;
  /** Called when the banner is clicked, after the window is focused. */
  onClick?: () => void;
}

/** Whether this browser can raise one at all. */
export function desktopNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export type DesktopNotificationPermission = 'granted' | 'denied' | 'default' | 'unsupported';

export function desktopNotificationPermission(): DesktopNotificationPermission {
  if (!desktopNotificationsSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Asks for permission, once.
 *
 * Never called on page load. Browsers increasingly refuse a request that is not
 * tied to a user gesture, and a permission prompt thrown at someone who has just
 * signed in is the fastest way to get a permanent "Block" — which cannot be
 * undone from script. Called when the officer opens Messages instead: they have
 * expressed interest in this feature by navigating to it.
 */
export async function requestDesktopNotifications(): Promise<DesktopNotificationPermission> {
  if (!desktopNotificationsSupported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    // Older Safari resolves this through a callback and rejects the promise
    // form. Not worth a shim: the caller treats anything but `granted` as off.
    return Notification.permission;
  }
}

/**
 * Raises a banner, if it would actually help.
 *
 * Suppressed while the document is visible: the officer is already looking at
 * the portal, where the badge, the conversation list and the open thread have
 * all updated in front of them. A banner for something they can already see is
 * pure noise, and it is the reason most people disable desktop notifications for
 * an app.
 */
export function showDesktopNotification(input: DesktopNotificationInput): void {
  if (desktopNotificationPermission() !== 'granted') return;
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;

  try {
    const notification = new Notification(input.title, {
      body: input.body,
      // The tag alone collapses repeats: a banner sharing it replaces the
      // previous one rather than stacking. (`renotify` would force a re-alert on
      // replace, which is the opposite of what is wanted and is not in the DOM
      // lib's `NotificationOptions` anyway.)
      tag: input.tag,
    });

    notification.onclick = () => {
      // Focus first: the click should land the officer in the portal, and a
      // route change behind a background window helps nobody.
      window.focus();
      notification.close();
      input.onClick?.();
    };
  } catch {
    // Some browsers throw when constructing one outside a service worker
    // (notably Android Chrome). Nothing here is load-bearing.
  }
}
